import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { setMachineContextDirect } from "../../../shared/chatBot/chatBotSlice";
import chatBotApi from "../../../shared/chatBot/ChatBotApi";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { SidebarState } from "../midasPageModels";
import { Machine } from "../../machinery/models/models";
import MidasSidebarHeader from "./MidasSidebarHeader";
import MidasFleetChips from "./MidasFleetChips";

// ===================================================================
//  Sidebar width constants
// ===================================================================

const SIDEBAR_EXPANDED = 280;
const SIDEBAR_COLLAPSED = 56;

// ===================================================================
//  MidasSidebar
// ===================================================================

interface MidasSidebarProps {
  sidebarState: SidebarState;
  onToggle: () => void;
  prefersReducedMotion?: boolean;
}

const MidasSidebar: React.FC<MidasSidebarProps> = ({
  sidebarState,
  onToggle,
  prefersReducedMotion = false,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const isExpanded = sidebarState === "expanded";
  const isHidden = sidebarState === "hidden";

  const width = isExpanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  // BUG-2026-03-20: Debounce the fire-and-forget setMachineContext API call.
  // When users click multiple machines rapidly, each click fired a separate
  // POST /v1/bot/chat/context — eating 6+ requests in 2 seconds and exhausting
  // the rate limit. Debounce ensures only the LAST machine click fires the API call.
  const contextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Fleet chip click: set machine context synchronously + fire bot API (debounced).
   * BUG FIX 2026-03-20: Uses setMachineContextDirect (sync) instead of the async
   * setMachineContext thunk. The async thunk has a gap between .pending and .fulfilled
   * where machineContext is null, causing MidasWelcome to flash the platform greeting.
   * The bot-side context API call is fired separately as fire-and-forget (debounced). */
  const handleMachineClick = useCallback(
    (machine: Machine) => {
      // Sync: set machineContext immediately so MidasWelcome renders machine greeting
      dispatch(
        setMachineContextDirect({
          machineId: machine.id,
          model: machine.model,
          serialNumber: machine.serialNumber,
          internalNumber: machine.internalNumber,
          system: machine.system,
          manufacturerName:
            machine.customBrandName || machine.manufacturer?.name || "",
          typeName: machine.type?.name,
          axisConfigName: machine.axisConfiguration?.name,
          controlSystemBrandName: machine.controlSystemBrand?.name,
          imageContent: machine.image?.content,
          description: machine.description,
          productionYear: machine.productionYear,
        })
      );
      // Fire-and-forget (debounced 300ms): tell the bot gateway to set machine context.
      // Only the last click in a rapid sequence actually fires the API call.
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
      contextTimerRef.current = setTimeout(() => {
        chatBotApi.setMachineContext(machine.id).catch(() => {
          // Best-effort — bot may be offline
        });
      }, 300);
    },
    [dispatch]
  );

  // F6.4: Sidebar expand/collapse transition (280px <-> 56px <-> 0)
  const sidebarTransition = prefersReducedMotion
    ? "none"
    : "width 0.28s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease";

  return (
    <Box
      role="navigation"
      aria-label={translate("midas.aria.sidebar") || "MIDAS sidebar navigation"}
      aria-expanded={isExpanded}
      sx={{
        width: isHidden ? 0 : `${width}px`,
        minWidth: isHidden ? 0 : `${width}px`,
        height: "100%",
        backgroundColor: dt.cardBg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: sidebarTransition,
        borderRight: isHidden ? "none" : `1px solid ${dt.cardBorder}`,
        flexShrink: 0,
      }}
    >
      <MidasSidebarHeader sidebarState={sidebarState} onToggle={onToggle} />

      {/* Fleet chips with integrated thread accordions */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.08) transparent",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.12)",
            borderRadius: "3px",
          },
        }}
      >
        <MidasFleetChips
          sidebarState={sidebarState}
          onMachineClick={handleMachineClick}
          prefersReducedMotion={prefersReducedMotion}
        />
      </Box>
    </Box>
  );
};

export default MidasSidebar;
